'use client';

import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { ApiClient } from '@/lib/api/client';
import type { ApiResponse } from '@/lib/api/types';
import { KAGEMANE_TEMPLATES, setKagemaneTemplates, type KagemaneTemplate } from './kagemane-catalog';

let snapshot = KAGEMANE_TEMPLATES;
let pending: Promise<KagemaneTemplate[]> | null = null;
// Only a successful API result (including an empty catalog) is authoritative.
let hasResolvedCatalog = false;
let resolvedAt = 0;
const CATALOG_TTL_MS = 30_000;
const listeners = new Set<() => void>();
export interface AdminKagemaneVideo extends KagemaneTemplate {
  isActive: boolean;
  origin: 'builtin' | 'upload';
}
export interface KagemanePublicationDetails {
  title?: string;
  reference_video_asset_id?: string;
  reference_image_asset_ids?: string[];
  prompt?: string;
}
class KagemaneCatalogApi extends ApiClient {
  async list(): Promise<ApiResponse<{ items: KagemaneTemplate[] }>> {
    // The catalog is public and identical for every account. Do not wait for
    // token recovery or add auth/build headers that force a CORS preflight.
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10_000);
    try {
      const response = await fetch(`${this.baseUrl}/video-replications/templates`, {
        credentials: 'omit', signal: controller.signal,
      });
      if (!response.ok) throw new Error('KAGEMANE_CATALOG_FAILED');
      return await response.json();
    } finally {
      clearTimeout(timeout);
    }
  }
  publish(assetId: string, details?: KagemanePublicationDetails) { return this.request<KagemaneTemplate>('/admin/kagemane/videos', { method: 'POST', body: JSON.stringify({ asset_id: assetId, ...details }) }); }
  publishTask(taskId: string) { return this.request<KagemaneTemplate>('/admin/kagemane/videos/from-task', { method: 'POST', body: JSON.stringify({ task_id: taskId }) }); }
  adminList() { return this.request<{ items: AdminKagemaneVideo[] }>('/admin/kagemane/videos'); }
  update(id: string, changes: { title?: string; isActive?: boolean }) {
    return this.request<AdminKagemaneVideo>(`/admin/kagemane/videos/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(changes) });
  }
  remove(id: string) { return this.request<{ id: string }>(`/admin/kagemane/videos/${encodeURIComponent(id)}`, { method: 'DELETE' }); }
}
export const kagemaneCatalogApi = new KagemaneCatalogApi();
const subscribe = (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; };
const subscribeToHydration = () => () => {};
const clientHydrated = () => true;
const serverHydrated = () => false;

export function refreshKagemaneCatalog(force = true): Promise<KagemaneTemplate[]> {
  if (pending) return pending;
  if (!force && hasResolvedCatalog && Date.now() - resolvedAt < CATALOG_TTL_MS) return Promise.resolve(snapshot);
  pending = (async () => {
    const response = await kagemaneCatalogApi.list();
    if (response.success !== true || !response.data) throw new Error('KAGEMANE_CATALOG_FAILED');
    snapshot = setKagemaneTemplates(response.data.items);
    hasResolvedCatalog = true;
    resolvedAt = Date.now();
    listeners.forEach((listener) => listener());
    return snapshot;
  })().finally(() => { pending = null; });
  return pending;
}

export function useKagemaneCatalog() {
  const items = useSyncExternalStore(subscribe, () => snapshot, () => KAGEMANE_TEMPLATES);
  // A sibling may warm the cache before this SSR boundary hydrates. During
  // hydration use the server loading frame; ordinary cached remounts stay ready.
  const hydrated = useSyncExternalStore(subscribeToHydration, clientHydrated, serverHydrated);
  const [loading, setLoading] = useState(() => !hasResolvedCatalog);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const retry = useCallback(() => setReloadKey((key) => key + 1), []);
  useEffect(() => {
    let active = true;
    const load = async () => {
      // Returning to this route revalidates in the background without hiding cards.
      setLoading(!hasResolvedCatalog);
      setError(false);
      try { await refreshKagemaneCatalog(reloadKey > 0); }
      catch { if (active) setError(true); }
      finally { if (active) setLoading(false); }
    };
    void load();
    return () => { active = false; };
  }, [reloadKey]);
  return { items, loading: !hydrated || loading, error, retry };
}
